import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import firebase from 'firebase/app';
import { UIMessagingService } from '../../services/uimessaging.service';
import { Facility, FirebaseUtilitiesService } from '../../services/firebase-utilities.service';
import { AuthService } from '../../services/auth.service';
import { UserRoles } from '../../dictionaries/UserRoles';

export interface CreateFacilityDialogData {
  facility?: Facility & { [key: string]: any };
}

interface OrthancOption {
  id: string;
  label: string;
}

const ORTHANC_FACILITIES: OrthancOption[] = [
  { id: 'SITE_A',     label: 'Site A' },
  { id: 'SITE_B',     label: 'Site B' },
  { id: 'SITE_C',     label: 'Site C' },
  { id: 'SITE_D',     label: 'Site D' },
  { id: 'SITE_E',     label: 'Site E' },
  { id: 'SITE_F',     label: 'Site F' },
  { id: 'SITE_G',     label: 'Site G' },
  { id: 'SITE_H',     label: 'Site H' },
  { id: 'SITE_I',     label: 'Site I' },
  { id: 'CUTEOTTERS', label: 'Cute Otters' },
];

@Component({
    selector: 'app-create-facility-form',
    templateUrl: './create-facility-form.component.html',
    styleUrls: ['./create-facility-form.component.scss'],
    standalone: false
})
export class CreateFacilityFormComponent implements OnInit {
  facilityForm: UntypedFormGroup;
  loading = false;
  deactivateLoading = false;
  isEditMode: boolean;
  isDisabled: boolean;
  availableOrthancFacilities: OrthancOption[] = ORTHANC_FACILITIES;
  orthancLoading = false;

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<CreateFacilityFormComponent>,
    private uiMessaging_$: UIMessagingService,
    private auth_$: AuthService,
    private firebaseUtilities_$: FirebaseUtilitiesService,
    @Inject(MAT_DIALOG_DATA) public data: CreateFacilityDialogData | null
  ) {
    this.isEditMode = !!(data?.facility);
    this.isDisabled = !!(data?.facility?.disabled);
    const f = data?.facility;
    this.facilityForm = this.fb.group({
      name: [f?.name || '', [Validators.required]],
      address: [f?.address || '', [Validators.required]],
      phoneNumber: [f?.phoneNumber || ''],
      emailAddress: [f?.emailAddress || '', [Validators.email]],
      planCode: [(f as any)?.planCode || 'spoke', [Validators.required]],
      orthancFacilityId: [f?.orthancFacilityId || null]
    });
  }

  get isSuperUser(): boolean {
    return this.auth_$.userData.getValue()?.['userDBRole'] === UserRoles.superuser;
  }

  get canDeactivate(): boolean {
    if (!this.isEditMode) return false;
    if (this.isSuperUser) return true;
    const uid = this.auth_$.userData.getValue()?.['uid'];
    const f = this.data?.facility as any;
    return !!uid && (uid === f?.ownerID || uid === f?.silentOwnerUid);
  }

  async ngOnInit(): Promise<void> {
    if (!this.isSuperUser) {
      this.facilityForm.get('planCode')?.disable();
    }
    if (this.isSuperUser && this.isEditMode) {
      await this.loadAvailableOrthancFacilities();
    }
  }

  private async loadAvailableOrthancFacilities(): Promise<void> {
    this.orthancLoading = true;
    try {
      const allFacilities = await this.firebaseUtilities_$.getFacilities();
      const currentId = this.data?.facility?.id;
      const taken = new Set(
        allFacilities
          .filter((f: any) => f.id !== currentId && f.orthancFacilityId)
          .map((f: any) => f.orthancFacilityId as string)
      );
      this.availableOrthancFacilities = ORTHANC_FACILITIES.filter(o => !taken.has(o.id));
    } catch {
      this.availableOrthancFacilities = ORTHANC_FACILITIES;
    } finally {
      this.orthancLoading = false;
    }
  }

  async onSubmit(): Promise<void> {
    if (this.facilityForm.invalid) {
      this.markFormGroupTouched(this.facilityForm);
      return;
    }

    this.loading = true;
    try {
      const formValue = this.facilityForm.getRawValue();

      if (this.isEditMode) {
        const facilityDocId = this.data!.facility!.id;
        const payload: any = {
          name: formValue.name.trim(),
          address: formValue.address.trim(),
          phoneNumber: formValue.phoneNumber?.trim() || '',
          emailAddress: formValue.emailAddress?.trim() || '',
          planCode: formValue.planCode,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp()
        };
        if (this.isSuperUser) {
          payload.orthancFacilityId = formValue.orthancFacilityId || null;
        }
        await firebase.firestore().collection('facilities').doc(facilityDocId).update(payload);
        this.uiMessaging_$.toastMessage('Facility updated successfully', 'SUCCESS');
      } else {
        const facilityName = formValue.name.trim();
        const facilityDocId = this.sanitizeName(facilityName);
        const facilityData = {
          name: facilityName,
          address: formValue.address.trim(),
          phoneNumber: formValue.phoneNumber?.trim() || '',
          emailAddress: formValue.emailAddress?.trim() || '',
          planCode: formValue.planCode,
          id: facilityDocId,
          status: 'pending',
          disabled: false,
          setup: 'minimal',
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          updatedAt: firebase.firestore.FieldValue.serverTimestamp()
        };
        await firebase.firestore().collection('facilities').doc(facilityDocId).set(facilityData);
        this.uiMessaging_$.toastMessage('Facility created successfully with status: pending', 'SUCCESS');
      }

      this.dialogRef.close(true);
    } catch (error) {
      console.error('Error saving facility:', error);
      this.uiMessaging_$.toastMessage('Error saving facility. Please try again.', 'ERROR');
    } finally {
      this.loading = false;
    }
  }

  async onDeactivate(): Promise<void> {
    const facilityDocId = this.data!.facility!.id;
    const newDisabled = !this.isDisabled;
    this.deactivateLoading = true;
    try {
      await firebase.firestore().collection('facilities').doc(facilityDocId).update({
        disabled: newDisabled,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp()
      });
      const msg = newDisabled ? 'Facility deactivated' : 'Facility reactivated';
      this.uiMessaging_$.toastMessage(msg, 'SUCCESS');
      this.dialogRef.close(true);
    } catch (error) {
      console.error('Error updating facility status:', error);
      this.uiMessaging_$.toastMessage('Error updating facility status. Please try again.', 'ERROR');
    } finally {
      this.deactivateLoading = false;
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  private markFormGroupTouched(formGroup: UntypedFormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private sanitizeName(name: string): string {
    return name.replace(/\s+/g, '-').toLowerCase();
  }
}
